(() => {
  const VALID_SORTS = new Set(['recommended', 'discount', 'price-asc', 'newest']);

  const coerceNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getSortParamName = (root) => root.dataset.sortParam || 'sale_sort';

  const getRequestedSortValue = (root) => {
    const params = new URLSearchParams(window.location.search);
    return params.get(getSortParamName(root));
  };

  const getSortValue = (root) => {
    const requestedSort = getRequestedSortValue(root);
    const defaultSort = root.dataset.defaultSort || 'recommended';
    return VALID_SORTS.has(requestedSort) ? requestedSort : defaultSort;
  };

  const syncUrlState = (root, { sortValue, persistSort = true, resetPage = false } = {}) => {
    const url = new URL(window.location.href);
    const paramName = getSortParamName(root);

    if (persistSort && sortValue) {
      url.searchParams.set(paramName, sortValue);
    }

    if (resetPage) {
      url.searchParams.delete('page');
    }

    window.history.replaceState({}, '', url);
  };

  const getCurrentPage = () => {
    const page = coerceNumber(new URLSearchParams(window.location.search).get('page'));
    return page > 0 ? page : 1;
  };

  const getTotalPages = (root) => coerceNumber(root.dataset.totalPages);

  const shouldHydrateAllPages = (root) => getTotalPages(root) > 1 && Boolean(root.dataset.sectionId);

  const createSectionPageUrl = (root, page) => {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('section_id', root.dataset.sectionId);

    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    return url.toString();
  };

  const readItemData = (element) => {
    const price = coerceNumber(element.dataset.price);
    const publishedAt = coerceNumber(element.dataset.publishedAt);
    const rating = coerceNumber(element.dataset.rating);
    const ratingCount = coerceNumber(element.dataset.ratingCount);
    const inventory = coerceNumber(element.dataset.inventory);
    const inventoryTracked = element.dataset.inventoryTracked === 'true';
    const ageInDays = publishedAt > 0 ? Math.max(0, (Date.now() / 1000 - publishedAt) / 86400) : 999;
    const freshnessSignal = Math.max(0, 45 - Math.min(ageInDays, 45)) * 0.22;
    const urgencySignal = inventoryTracked ? (inventory > 0 && inventory <= 5 ? 4 : inventory <= 10 ? 2 : 0.75) : 0;
    const affordabilitySignal = Math.max(0, 20 - Math.min(price / 10000, 20)) * 0.18;
    const discount = coerceNumber(element.dataset.discount);
    const recommendedScore =
      discount * 0.58 +
      rating * 7 +
      Math.min(ratingCount, 40) * 0.75 +
      freshnessSignal +
      urgencySignal +
      affordabilitySignal;

    return {
      element,
      originalIndex: coerceNumber(element.dataset.originalIndex),
      price,
      discount,
      publishedAt,
      rating,
      ratingCount,
      recommendedScore,
    };
  };

  const compareNumbers = (left, right) => {
    if (left < right) return -1;
    if (left > right) return 1;
    return 0;
  };

  const sortEntries = (entries, sortValue) => {
    const sortedEntries = [...entries];

    sortedEntries.sort((left, right) => {
      if (sortValue === 'discount') {
        return (
          compareNumbers(right.discount, left.discount) ||
          compareNumbers(right.recommendedScore, left.recommendedScore) ||
          compareNumbers(left.price, right.price) ||
          compareNumbers(left.originalIndex, right.originalIndex)
        );
      }

      if (sortValue === 'price-asc') {
        return (
          compareNumbers(left.price, right.price) ||
          compareNumbers(right.discount, left.discount) ||
          compareNumbers(right.recommendedScore, left.recommendedScore) ||
          compareNumbers(left.originalIndex, right.originalIndex)
        );
      }

      if (sortValue === 'newest') {
        return (
          compareNumbers(right.publishedAt, left.publishedAt) ||
          compareNumbers(right.recommendedScore, left.recommendedScore) ||
          compareNumbers(right.discount, left.discount) ||
          compareNumbers(left.originalIndex, right.originalIndex)
        );
      }

      return (
        compareNumbers(right.recommendedScore, left.recommendedScore) ||
        compareNumbers(right.discount, left.discount) ||
        compareNumbers(right.ratingCount, left.ratingCount) ||
        compareNumbers(left.price, right.price) ||
        compareNumbers(left.originalIndex, right.originalIndex)
      );
    });

    return sortedEntries;
  };

  const updateButtons = (root, sortValue) => {
    root.querySelectorAll('[data-sale-sort-button]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.sortValue === sortValue ? 'true' : 'false');
    });
  };

  const setButtonsPending = (root, isPending) => {
    root.querySelectorAll('[data-sale-sort-button]').forEach((button) => {
      button.disabled = isPending;
      button.setAttribute('aria-busy', isPending ? 'true' : 'false');
    });
  };

  const setSectionPending = (root, isPending) => {
    root.dataset.salePending = isPending ? 'true' : 'false';
    root.setAttribute('aria-busy', isPending ? 'true' : 'false');
  };

  const isFullyHydrated = (root) => root.dataset.saleHydrated === 'true';

  const updateEmptyState = (root) => {
    const emptyState = root.querySelector('[data-sale-empty]');
    if (!emptyState) return;

    const grid = root.querySelector('[data-sale-grid]');
    const itemCount = grid ? grid.querySelectorAll('[data-sale-item]').length : 0;
    const isPending = root.dataset.salePending === 'true';
    emptyState.hidden = isPending || itemCount > 0;
  };

  const reindexItems = (root) => {
    root.querySelectorAll('[data-sale-item]').forEach((item, index) => {
      item.dataset.originalIndex = String(index + 1);
    });
  };

  const applySort = (root, sortValue) => {
    const grid = root.querySelector('[data-sale-grid]');
    if (!grid) return;

    const entries = Array.from(grid.querySelectorAll('[data-sale-item]')).map(readItemData);
    const sortedEntries = sortEntries(entries, sortValue);

    sortedEntries.forEach((entry) => {
      entry.element.hidden = false;
      grid.appendChild(entry.element);
    });

    updateButtons(root, sortValue);
    root.dataset.activeSort = sortValue;
    updateEmptyState(root);
  };

  const parseSaleItems = (markup) => {
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(markup, 'text/html');
    return Array.from(parsedDocument.querySelectorAll('[data-sale-item]'));
  };

  const updatePaginationVisibility = (root) => {
    const pagination = root.querySelector('[data-sale-pagination]');
    if (!pagination) return;

    pagination.hidden = root.dataset.salePending === 'true' || isFullyHydrated(root);
  };

  const loadAllSaleItems = async (root) => {
    const totalPages = getTotalPages(root);
    if (totalPages <= 1 || !root.dataset.sectionId) {
      root.dataset.saleHydrationFailed = 'false';
      root.dataset.saleHydrated = 'true';
      reindexItems(root);
      updateEmptyState(root);
      updatePaginationVisibility(root);
      return true;
    }

    const grid = root.querySelector('[data-sale-grid]');
    if (!grid) return false;

    const currentPage = getCurrentPage();
    const pageBuckets = new Map([[currentPage, Array.from(grid.querySelectorAll('[data-sale-item]'))]]);
    const pagesToFetch = [];

    for (let page = 1; page <= totalPages; page += 1) {
      if (page === currentPage) continue;
      pagesToFetch.push(page);
    }

    try {
      const fetchedPages = await Promise.all(
        pagesToFetch.map(async (page) => {
          const response = await fetch(createSectionPageUrl(root, page), {
            credentials: 'same-origin',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to load page ${page}: ${response.status}`);
          }

          const markup = await response.text();
          const importedItems = parseSaleItems(markup).map((item) => document.importNode(item, true));
          return { page, items: importedItems };
        })
      );

      fetchedPages.forEach(({ page, items }) => {
        pageBuckets.set(page, items);
      });

      grid.innerHTML = '';

      for (let page = 1; page <= totalPages; page += 1) {
        const items = pageBuckets.get(page) || [];
        items.forEach((item) => {
          grid.appendChild(item);
        });
      }

      root.dataset.saleHydrationFailed = 'false';
      root.dataset.saleHydrated = 'true';
      reindexItems(root);
      updateEmptyState(root);
      updatePaginationVisibility(root);
      return true;
    } catch (error) {
      // Error handled silently in production - sale items aggregation failed
      root.dataset.saleHydrationFailed = 'true';
      root.dataset.saleHydrated = 'false';
      reindexItems(root);
      updateEmptyState(root);
      updatePaginationVisibility(root);
      return false;
    }
  };

  const ensureHydratedForSort = (root) => {
    if (isFullyHydrated(root)) return Promise.resolve(true);
    if (root.saleHydrationPromise) return root.saleHydrationPromise;

    root.saleHydrationPromise = loadAllSaleItems(root).finally(() => {
      root.saleHydrationPromise = null;
    });

    return root.saleHydrationPromise;
  };

  const initSaleSection = async (root) => {
    if (!root || root.dataset.saleInitialized === 'true') return;

    root.dataset.saleInitialized = 'true';
    root.dataset.saleHydrated = shouldHydrateAllPages(root) ? 'false' : 'true';
    root.dataset.saleHydrationFailed = 'false';
    setSectionPending(root, false);
    updatePaginationVisibility(root);
    reindexItems(root);
    updateEmptyState(root);

    root.querySelectorAll('[data-sale-sort-button]').forEach((button) => {
      button.addEventListener('click', async () => {
        const sortValue = button.dataset.sortValue;
        if (!VALID_SORTS.has(sortValue)) return;

        if (!isFullyHydrated(root) && shouldHydrateAllPages(root)) {
          setSectionPending(root, true);
          setButtonsPending(root, true);
          updatePaginationVisibility(root);
          updateEmptyState(root);

          try {
            await ensureHydratedForSort(root);
          } finally {
            setButtonsPending(root, false);
            setSectionPending(root, false);
            updatePaginationVisibility(root);
            updateEmptyState(root);
          }
        }

        applySort(root, sortValue);
        syncUrlState(root, {
          sortValue,
          persistSort: true,
          resetPage: isFullyHydrated(root),
        });
      });
    });

    const initialSort = getSortValue(root);

    if (shouldHydrateAllPages(root)) {
      setSectionPending(root, true);
      setButtonsPending(root, true);
      updatePaginationVisibility(root);
      updateEmptyState(root);

      try {
        const hydrated = await ensureHydratedForSort(root);
        if (hydrated && (getCurrentPage() > 1 || getRequestedSortValue(root) !== null)) {
          syncUrlState(root, {
            sortValue: initialSort,
            persistSort: getRequestedSortValue(root) !== null,
            resetPage: true,
          });
        }
      } finally {
        setButtonsPending(root, false);
        setSectionPending(root, false);
        updatePaginationVisibility(root);
        updateEmptyState(root);
      }
    }

    applySort(root, initialSort);
  };

  const initAllSaleSections = (container = document) => {
    container.querySelectorAll('[data-simple-sale-products]').forEach((root) => {
      initSaleSection(root).catch(() => {
        // Error handled silently in production
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllSaleSections());
  } else {
    initAllSaleSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAllSaleSections(event.target);
  });
})();
